import { useAuth } from '@clerk/react';
import { isWritableSubscriptionStatus } from '@prospero/shared';
import { useQuery } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { useMemo } from 'react';
import { SubscriptionService } from '../../services/subscription/subscription.service';
import type {
  ISubscriptionService,
  NextCharge,
} from '../../services/subscription/subscription.service.interface';
import { getCalendarDaysUntil } from '../../utils/days-until';
import { useValidatedTenantId } from '../auth/use-validated-tenant-id';
import { subscriptionQueryKeys } from './query-keys';

const FIVE_MINUTES = 5 * 60 * 1000;

export type BillingViewState =
  | 'TRIAL_MONTHLY'
  | 'TRIAL_ANNUAL'
  | 'TRIAL_NO_COMMITMENT'
  | 'ACTIVE_MONTHLY'
  | 'ACTIVE_ANNUAL'
  | 'PAST_DUE_MONTHLY'
  | 'PAST_DUE_ANNUAL'
  | 'CANCELED_AFTER_TRIAL'
  | 'CANCELED_SUBSCRIPTION'
  | 'EXPIRED'
  | 'PARTNER_COMP'
  | 'NO_PLAN';

/**
 * A TRIALING tenant whose "trial" is really the period it already paid for:
 * after a re-subscribe the server hands that period to Stripe as `trial_end`,
 * so the status reads TRIALING while the tenant is fully paid. The future
 * check is defensive — a stale cached response must never revive the trial UI.
 */
export function computeIsCreditedPeriod(
  subscriptionStatus: string | null,
  creditedUntil: string | null,
): boolean {
  return (
    subscriptionStatus === 'TRIALING' &&
    creditedUntil != null &&
    new Date(creditedUntil).getTime() > Date.now()
  );
}

function resolveTrialingViewState(input: {
  isAnnual: boolean;
  hasStripeSubscription: boolean;
  isCreditedPeriod: boolean;
}): BillingViewState {
  // No Stripe subscription means there is no commitment to charge at trial end
  // (the standard card-less trial). The UI must not promise an
  // automatic renewal that won't happen.
  if (!input.hasStripeSubscription) {
    return 'TRIAL_NO_COMMITMENT';
  }
  // An already-paid period is not a trial: presenting it as one tells a paying
  // customer their subscription was not renewed.
  if (input.isCreditedPeriod) {
    return input.isAnnual ? 'ACTIVE_ANNUAL' : 'ACTIVE_MONTHLY';
  }
  return input.isAnnual ? 'TRIAL_ANNUAL' : 'TRIAL_MONTHLY';
}

export function deriveBillingViewState(input: {
  subscriptionStatus: string | null;
  billingInterval: string | null;
  planTier: string | null;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  creditedUntil: string | null;
  hasStripeSubscription: boolean;
  isPartnerComp?: boolean;
}): BillingViewState {
  const {
    subscriptionStatus,
    billingInterval,
    planTier,
    trialEndsAt,
    currentPeriodEnd,
    creditedUntil,
    hasStripeSubscription,
  } = input;

  if (planTier == null || subscriptionStatus == null) {
    return 'NO_PLAN';
  }

  if (input.isPartnerComp) {
    return 'PARTNER_COMP';
  }

  const isAnnual = billingInterval === 'ANNUAL';

  switch (subscriptionStatus) {
    case 'TRIALING':
      return resolveTrialingViewState({
        isAnnual,
        hasStripeSubscription,
        isCreditedPeriod: computeIsCreditedPeriod(subscriptionStatus, creditedUntil),
      });
    case 'ACTIVE':
      return isAnnual ? 'ACTIVE_ANNUAL' : 'ACTIVE_MONTHLY';
    case 'PAST_DUE':
      return isAnnual ? 'PAST_DUE_ANNUAL' : 'PAST_DUE_MONTHLY';
    case 'CANCELED':
      if (trialEndsAt != null && currentPeriodEnd == null) {
        return 'CANCELED_AFTER_TRIAL';
      }
      return 'CANCELED_SUBSCRIPTION';
    case 'EXPIRED':
      return 'EXPIRED';
    default:
      return 'NO_PLAN';
  }
}

/**
 * The past-due grace runs until its end instant; the days left are counted in
 * calendar days like every other countdown, so the last day reads 0.
 */
export function computePastDueGraceInfo(
  pastDueGraceUntil: string | null,
  now: Date = new Date(),
): { isInGracePeriod: boolean; pastDueDaysRemaining: number | null } {
  return {
    isInGracePeriod:
      pastDueGraceUntil != null && new Date(pastDueGraceUntil).getTime() > now.getTime(),
    pastDueDaysRemaining: getCalendarDaysUntil(pastDueGraceUntil, now),
  };
}

/**
 * Frontend hint for "can this user write right now?". Backend SubscriptionGuard
 * is the authoritative check; this flag only drives banner visibility and
 * read-only gates. We MUST default to true while the subscription is still
 * loading (status === null) — otherwise TRIALING/ACTIVE users see a fake
 * "subscription locked" banner and write buttons disappear until the query
 * resolves (and sometimes indefinitely, if memberships never hydrate).
 *
 * The writable-status set is shared with the backend via
 * `@prospero/shared` → `WRITABLE_SUBSCRIPTION_STATUSES`. Do NOT inline it.
 */
export function deriveCanWrite(subscriptionStatus: string | null): boolean {
  if (subscriptionStatus === null) {
    return true;
  }
  return isWritableSubscriptionStatus(subscriptionStatus);
}

export function computeIsTrialExpiringSoon(
  subscriptionStatus: string | null,
  trialEndsAt: string | null,
  creditedUntil: string | null,
): boolean {
  if (subscriptionStatus !== 'TRIALING' || !trialEndsAt) {
    return false;
  }
  // An already-paid period is not expiring: `trialEndsAt` here is the stale
  // date of the tenant's original trial, not the end of its paid coverage.
  if (computeIsCreditedPeriod(subscriptionStatus, creditedUntil)) {
    return false;
  }
  const daysRemaining = getCalendarDaysUntil(trialEndsAt);
  return daysRemaining !== null && daysRemaining <= 3;
}

interface UseSubscriptionFields {
  subscriptionStatus: string | null;
  planTier: string | null;
  billingInterval: string | null;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  creditedUntil: string | null;
  /** First charge of the billing-page checkout; null = charged today. */
  embeddedCheckoutFirstChargeAt: string | null;
  pastDueGraceUntil: string | null;
  nextPaymentAttempt: string | null;
  paymentActionUrl: string | null;
  paymentActionRequiredAt: string | null;
  aiExtractionLimit: number;
  currentWeekUsage: number;
  weekPeriodEnd: string | null;
  appliedDiscountCode: string | null;
  discountPercentOff: number | null;
  discountDurationInMonths: number | null;
  /** First paid invoice carrying the coupon: the start of its window. */
  discountAppliedAt: string | null;
  /** What the live subscription charges next, as computed by the server. */
  nextCharge: NextCharge | null;
  cancelAtPeriodEnd: boolean;
  hasStripeSubscription: boolean;
  isPartnerComp: boolean;
  ddtAddonEnabled: boolean;
  surveyAddonEnabled: boolean;
  nutritionAddonEnabled: boolean;
  prepackagedLabelEnabled: boolean;
  iotAddonEnabled: boolean;
  menuAddonEnabled: boolean;
  stripeEnabled: boolean;
  referralAnnualOnly: boolean;
  referralDiscountCode: string | null;
  referralDiscountPercentOff: number | null;
  referralDiscountDurationInMonths: number | null;
}

const SUBSCRIPTION_FIELD_KEYS = [
  'subscriptionStatus',
  'planTier',
  'billingInterval',
  'trialEndsAt',
  'currentPeriodEnd',
  'creditedUntil',
  'embeddedCheckoutFirstChargeAt',
  'pastDueGraceUntil',
  'nextPaymentAttempt',
  'paymentActionUrl',
  'paymentActionRequiredAt',
  'weekPeriodEnd',
  'appliedDiscountCode',
  'discountAppliedAt',
  'referralDiscountCode',
] as const satisfies readonly (keyof UseSubscriptionFields)[];

const SUBSCRIPTION_NUMBER_FIELD_KEYS = [
  'aiExtractionLimit',
  'currentWeekUsage',
  'discountPercentOff',
  'discountDurationInMonths',
  'referralDiscountPercentOff',
  'referralDiscountDurationInMonths',
] as const satisfies readonly (keyof UseSubscriptionFields)[];

const SUBSCRIPTION_BOOLEAN_FIELD_KEYS = [
  'cancelAtPeriodEnd',
  'hasStripeSubscription',
  'isPartnerComp',
  'ddtAddonEnabled',
  'surveyAddonEnabled',
  'nutritionAddonEnabled',
  'prepackagedLabelEnabled',
  'iotAddonEnabled',
  'menuAddonEnabled',
  'stripeEnabled',
  'referralAnnualOnly',
] as const satisfies readonly (keyof UseSubscriptionFields)[];

function defaultSubscriptionFields(): UseSubscriptionFields {
  return {
    subscriptionStatus: null,
    planTier: null,
    billingInterval: null,
    trialEndsAt: null,
    currentPeriodEnd: null,
    creditedUntil: null,
    embeddedCheckoutFirstChargeAt: null,
    pastDueGraceUntil: null,
    nextPaymentAttempt: null,
    paymentActionUrl: null,
    paymentActionRequiredAt: null,
    aiExtractionLimit: 70,
    currentWeekUsage: 0,
    weekPeriodEnd: null,
    appliedDiscountCode: null,
    discountPercentOff: null,
    discountDurationInMonths: null,
    discountAppliedAt: null,
    nextCharge: null,
    cancelAtPeriodEnd: false,
    hasStripeSubscription: false,
    isPartnerComp: false,
    ddtAddonEnabled: false,
    surveyAddonEnabled: false,
    nutritionAddonEnabled: false,
    prepackagedLabelEnabled: false,
    iotAddonEnabled: false,
    menuAddonEnabled: false,
    // Safe default: missing field must never hide Stripe UI in prod/dev.
    stripeEnabled: true,
    referralAnnualOnly: false,
    referralDiscountCode: null,
    referralDiscountPercentOff: null,
    referralDiscountDurationInMonths: null,
  };
}

function pickSubscriptionFields(
  subscription: { [key: string]: unknown } | null,
): UseSubscriptionFields {
  const result = defaultSubscriptionFields();

  if (!subscription) {
    return result;
  }

  for (const key of SUBSCRIPTION_FIELD_KEYS) {
    const val = subscription[key];
    if (typeof val === 'string') {
      result[key] = val;
    }
  }

  for (const key of SUBSCRIPTION_NUMBER_FIELD_KEYS) {
    const val = subscription[key];
    if (typeof val === 'number') {
      result[key] = val;
    }
  }

  for (const key of SUBSCRIPTION_BOOLEAN_FIELD_KEYS) {
    const val = subscription[key];
    if (typeof val === 'boolean') {
      result[key] = val;
    }
  }

  result.nextCharge = parseNextCharge(subscription.nextCharge);

  return result;
}

function isRecord(value: unknown): value is { [key: string]: unknown } {
  return typeof value === 'object' && value !== null;
}

export function parseNextCharge(value: unknown): NextCharge | null {
  if (!isRecord(value)) {
    return null;
  }
  const { amountCents, at, isDiscounted } = value;
  if (typeof amountCents !== 'number' || typeof isDiscounted !== 'boolean') {
    return null;
  }
  return { amountCents, at: typeof at === 'string' ? at : null, isDiscounted };
}

function useSubscriptionComputed(fields: UseSubscriptionFields) {
  const { isInGracePeriod, pastDueDaysRemaining } = useMemo(
    () => computePastDueGraceInfo(fields.pastDueGraceUntil),
    [fields.pastDueGraceUntil],
  );

  const isCreditedPeriod = useMemo(
    () => computeIsCreditedPeriod(fields.subscriptionStatus, fields.creditedUntil),
    [fields.subscriptionStatus, fields.creditedUntil],
  );

  const isTrialExpiringSoon = useMemo(
    () =>
      computeIsTrialExpiringSoon(
        fields.subscriptionStatus,
        fields.trialEndsAt,
        fields.creditedUntil,
      ),
    [fields.subscriptionStatus, fields.trialEndsAt, fields.creditedUntil],
  );

  const billingViewState = useMemo<BillingViewState>(
    () =>
      deriveBillingViewState({
        subscriptionStatus: fields.subscriptionStatus,
        billingInterval: fields.billingInterval,
        planTier: fields.planTier,
        trialEndsAt: fields.trialEndsAt,
        currentPeriodEnd: fields.currentPeriodEnd,
        creditedUntil: fields.creditedUntil,
        hasStripeSubscription: fields.hasStripeSubscription,
        isPartnerComp: fields.isPartnerComp,
      }),
    [
      fields.subscriptionStatus,
      fields.billingInterval,
      fields.planTier,
      fields.trialEndsAt,
      fields.currentPeriodEnd,
      fields.creditedUntil,
      fields.hasStripeSubscription,
      fields.isPartnerComp,
    ],
  );

  const trialDaysRemaining = useMemo(
    () => getCalendarDaysUntil(fields.trialEndsAt),
    [fields.trialEndsAt],
  );

  return {
    isInGracePeriod,
    pastDueDaysRemaining,
    isCreditedPeriod,
    isTrialExpiringSoon,
    billingViewState,
    trialDaysRemaining,
  };
}

export function useSubscription() {
  const subscriptionService = useInjection<ISubscriptionService>(SubscriptionService);
  const { isSignedIn } = useAuth();
  const currentTenantId = useValidatedTenantId();

  const {
    data: fullData,
    isLoading,
    isPending,
    fetchStatus,
    isError,
    refetch,
  } = useQuery({
    queryKey: subscriptionQueryKeys.full(),
    queryFn: () => subscriptionService.getFullSubscription(),
    staleTime: FIVE_MINUTES,
    refetchOnWindowFocus: true,
    enabled: !!isSignedIn && !!currentTenantId,
  });

  const subscription = fullData?.subscription ?? null;
  const planModulesConfig = fullData?.planModulesConfig;
  const fields = pickSubscriptionFields(subscription as { [key: string]: unknown } | null);
  const computed = useSubscriptionComputed(fields);

  const canWrite = deriveCanWrite(fields.subscriptionStatus);
  const isPastDue = fields.subscriptionStatus === 'PAST_DUE';
  const hasScheduledRetry = !!fields.nextPaymentAttempt;
  const requiresPaymentAction = !!fields.paymentActionUrl;

  return {
    subscription,
    isLoading,
    // `isPending` is the flag route guards must gate on: a query disabled until
    // the tenant is validated reports `isLoading: false` with no data, and a
    // guard reading `isLoading` would redirect on a not-yet-fetched answer.
    isPending,
    // Disambiguates a permanently-disabled query (`fetchStatus: 'idle'`) from
    // one actually in flight — see `isGuardQuerySettling` in
    // `router/guards/guard-query-state.ts`.
    fetchStatus,
    isError,
    refetch,
    planModulesConfig,
    canWrite,
    isPastDue,
    ...fields,
    ...computed,
    hasScheduledRetry,
    requiresPaymentAction,
  };
}
